import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ConfirmModal } from '../components/ConfirmModal';
import { EmptyState } from '../components/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Search, Plus, Users, AlertTriangle, Pencil, Trash2, ChevronLeft, ChevronRight, SearchX, NotebookPen, Flame } from 'lucide-react';

import { API } from '@/lib/api';
import { cn } from '@/lib/utils';
import { avatarTint, iniciales } from '@/lib/identity';
import { PatientFormDialog } from '../components/patients/PatientFormDialog';
import { SeguimientoDialog } from '../components/patients/SeguimientoDialog';

const METODO_PAGO_LABEL = {
  efectivo:      'Efectivo',
  tarjeta:       'Tarjeta',
  transferencia: 'Transferencia',
};

export const PatientsPage = ({ mode = 'clientes' }) => {
  const esProspectos = mode === 'prospectos';
  const navigate = useNavigate();
  const location = useLocation();
  const PAGE_SIZE = 50;
  const [searchTerm, setSearchTerm]     = useState('');
  const [searchInput, setSearchInput]   = useState('');
  const [patients, setPatients]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(0); // 0-based
  const [hasMore, setHasMore]           = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [soloAtendidos, setSoloAtendidos] = useState(false);
  const [soloConFalta, setSoloConFalta] = useState(false);
  const [filtroSeguimiento, setFiltroSeguimiento] = useState(''); // '' | 'sin' | 'con'
  const [seguimientoPatient, setSeguimientoPatient] = useState(null);
  const [totalCount, setTotalCount]     = useState(0);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  // Acción disparada desde ⌘K o desde el topbar ("Nuevo paciente").
  useEffect(() => {
    if (location.state?.accion === 'nuevo-paciente') {
      setIsDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const fetchPatients = useCallback(async (currentPage = 0, search = '', atendidos = false, conFalta = false, seguimientoF = '') => {
    setLoading(true);
    const params = {
      search: search || undefined,
      solo_atendidos: atendidos || undefined,
      solo_prospectos: esProspectos || undefined,
      solo_clientes: esProspectos ? undefined : true,
      con_falta: esProspectos ? (conFalta || undefined) : undefined,
      seguimiento: esProspectos ? (seguimientoF || undefined) : undefined,
    };
    try {
      const [res, countRes] = await Promise.all([
        axios.get(`${API}/patients`, { params: { ...params, skip: currentPage * PAGE_SIZE, limit: PAGE_SIZE } }),
        axios.get(`${API}/patients/count`, { params }),
      ]);
      setPatients(res.data);
      setHasMore(res.data.length === PAGE_SIZE);
      setTotalCount(countRes.data.total);
    } catch {
      toast.error(esProspectos ? 'Error al cargar prospectos' : 'Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  }, [esProspectos]);

  useEffect(() => { fetchPatients(0, ''); }, [fetchPatients]);

  // Búsqueda con debounce de 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
      fetchPatients(0, searchInput, soloAtendidos, soloConFalta, filtroSeguimiento);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, soloAtendidos, soloConFalta, filtroSeguimiento, fetchPatients]);

  // Los efectos de arriba refetchean al cambiar estos filtros
  const toggleAtendidos = () => setSoloAtendidos(prev => !prev);
  const toggleConFalta = () => setSoloConFalta(prev => !prev);
  const cambiarFiltroSeguimiento = (valor) => setFiltroSeguimiento(prev => prev === valor ? '' : valor);

  const openNewPatient = () => {
    setEditingPatient(null);
    setIsDialogOpen(true);
  };

  const openEditPatient = (patient) => {
    setEditingPatient(patient);
    setIsDialogOpen(true);
  };

  // Tras guardar, recarga la página actual conservando la búsqueda activa.
  const handleSaved = () => fetchPatients(page, searchTerm, soloAtendidos, soloConFalta, filtroSeguimiento);

  const handleDeleteClick = (patient, e) => {
    e.stopPropagation();
    setPatientToDelete(patient);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!patientToDelete) return;
    try {
      await axios.delete(`${API}/patients/${patientToDelete.id}`);
      toast.success(`Paciente ${patientToDelete.nombre} ${patientToDelete.apellido} eliminado`);
      setPatients(prev => prev.filter(p => p.id !== patientToDelete.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar paciente');
    }
    setPatientToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const toggleReto = async (patient) => {
    const activo = !patient.reto_21_activo;
    try {
      const res = await axios.put(`${API}/patients/${patient.id}/reto21`, { activo });
      // El PUT no trae tiene_plan/ultima_atencion (solo se calculan en el GET
      // de la lista): tomamos solo los campos del reto, no pisamos el resto.
      setPatients(prev => prev.map(p => p.id === patient.id
        ? { ...p, reto_21_activo: res.data.reto_21_activo, reto_21_fecha_inicio: res.data.reto_21_fecha_inicio }
        : p
      ));
      toast.success(activo ? `${patient.nombre} entró al reto de 21 días` : `${patient.nombre} salió del reto`);
    } catch {
      toast.error('Error al actualizar el reto de 21 días');
    }
  };

  // La búsqueda es server-side — patients ya viene filtrado
  const filteredPatients = patients;
  const patientsWithAlerts = patients.filter(p => p.alertas_medicas?.length > 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="patients-page">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-display text-[26px] font-extrabold leading-tight tracking-tight md:text-[30px]">
              {esProspectos ? 'Prospectos' : 'Pacientes'}
            </h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              {esProspectos
                ? 'Personas que aún no tienen cita atendida ni plan alimenticio'
                : 'Con al menos una cita atendida o un plan alimenticio'}
            </p>
          </div>
          <Button
            onClick={openNewPatient}
            data-testid="add-patient-btn"
            className="h-9 gap-1.5 bg-brand-gradient text-white shadow-[var(--shadow-glow)] hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {esProspectos ? 'Nuevo prospecto' : 'Nuevo paciente'}
          </Button>
        </div>

        {/* Barra de herramientas: buscar y filtrar sin salir de la lista */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o teléfono…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9 bg-card pl-9"
              data-testid="search-patients"
            />
          </div>

          <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-[12px] font-medium text-muted-foreground ring-1 ring-inset ring-border">
            <Users className="h-3.5 w-3.5" />
            <span className="tnum font-semibold text-foreground">{totalCount}</span>
            {esProspectos ? 'prospectos' : 'pacientes'}
          </span>

          {patientsWithAlerts.length > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-rose-500/10 px-2.5 py-1 text-[12px] font-medium text-rose-700 ring-1 ring-inset ring-rose-500/25 dark:text-rose-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="tnum font-semibold">{patientsWithAlerts.length}</span> con alertas médicas
            </span>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {!esProspectos && (
              <FiltroChip active={soloAtendidos} onClick={toggleAtendidos} testid="filter-atendidos">
                Solo atendidos
              </FiltroChip>
            )}
            {esProspectos && (
              <>
                <FiltroChip active={soloConFalta} onClick={toggleConFalta} testid="filter-con-falta">
                  Faltaron a su cita
                </FiltroChip>
                <FiltroChip active={filtroSeguimiento === 'sin'} onClick={() => cambiarFiltroSeguimiento('sin')} testid="filter-nuevos">
                  Nuevos
                </FiltroChip>
                <FiltroChip active={filtroSeguimiento === 'con'} onClick={() => cambiarFiltroSeguimiento('con')} testid="filter-ya-contactados">
                  Ya contactados
                </FiltroChip>
              </>
            )}
            {searchInput && (
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setSearchInput('')}>
                Limpiar
              </Button>
            )}
          </div>
        </div>

        {/* Patients List */}
        <Card className="bg-card border-border/70 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/70 px-4 py-3">
            <CardTitle className="font-display text-[15px] font-bold tracking-tight">
              {searchInput
                ? `Resultados para «${searchInput}»`
                : `${esProspectos ? 'Prospectos' : 'Pacientes'} · página ${page + 1}`}
            </CardTitle>
            {filteredPatients.length > 0 && (
              <span className="text-[12px] text-muted-foreground tnum">
                {filteredPatients.length} en esta página
              </span>
            )}
          </CardHeader>
          <CardContent className="p-4">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="p-4 rounded-xl border border-border/60 bg-card">
                    <div className="flex items-start gap-3">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1 space-y-2 pt-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredPatients.length === 0 ? (
              <EmptyState
                icon={searchInput ? SearchX : Users}
                title={searchInput ? 'Sin resultados' : (esProspectos ? 'Aún no hay prospectos' : 'Aún no hay pacientes')}
                description={searchInput
                  ? 'No encontramos resultados con ese término. Prueba con otro nombre o teléfono.'
                  : (esProspectos
                    ? 'Los pacientes sin cita atendida ni plan alimenticio aparecerán aquí.'
                    : 'Da de alta a tu primer paciente para empezar a gestionar su expediente.')}
                actionLabel={searchInput ? undefined : (esProspectos ? 'Nuevo prospecto' : 'Nuevo paciente')}
                onAction={searchInput ? undefined : openNewPatient}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  className="p-4 rounded-xl border border-border/70 bg-card shadow-sm hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md transition-all duration-200 group"
                  data-testid={`patient-${patient.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar
                      className="h-11 w-11 shrink-0 cursor-pointer"
                      style={avatarTint(`${patient.nombre} ${patient.apellido}`)}
                      onClick={() => navigate(`/patients/${patient.id}`)}
                    >
                      <AvatarImage src={patient.avatar_url} alt={`${patient.nombre} ${patient.apellido}`} />
                      <AvatarFallback className="text-[12px] font-semibold text-white" style={avatarTint(`${patient.nombre} ${patient.apellido}`)}>
                        {iniciales(`${patient.nombre} ${patient.apellido}`)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <p
                        className="cursor-pointer text-[14px] font-semibold leading-tight hover:text-primary"
                        onClick={() => navigate(`/patients/${patient.id}`)}
                      >
                        {patient.nombre} {patient.apellido}
                      </p>
                      <p className="mt-1 text-[12.5px] text-muted-foreground tnum whitespace-nowrap">{patient.telefono}</p>
                      {patient.email && (
                        <p className="truncate text-[12px] text-muted-foreground">{patient.email}</p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      {patient.alertas_medicas?.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-500/25 dark:text-rose-300"
                          title={patient.alertas_medicas.map((a) => a.descripcion).join(' · ')}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          {patient.alertas_medicas.length}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditPatient(patient);
                        }}
                        data-testid={`edit-patient-${patient.id}`}
                        aria-label={`Editar ${patient.nombre} ${patient.apellido}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-destructive hover:text-destructive"
                        onClick={(e) => handleDeleteClick(patient, e)}
                        data-testid={`delete-patient-${patient.id}`}
                        aria-label={`Eliminar ${patient.nombre} ${patient.apellido}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Atención y cobro: se calculan para toda la vista de Pacientes (clientes reales) */}
                  {!esProspectos && (
                    <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center gap-2">
                      <Badge
                        role="button"
                        onClick={(e) => { e.stopPropagation(); toggleReto(patient); }}
                        className={cn(
                          'flex-shrink-0 cursor-pointer select-none',
                          patient.reto_21_activo
                            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                            : 'bg-muted text-muted-foreground hover:bg-muted/70'
                        )}
                        title={patient.reto_21_activo ? 'En el reto de 21 días — clic para quitar' : 'Clic para agregar al reto de 21 días'}
                        data-testid={`reto21-badge-${patient.id}`}
                      >
                        <Flame className="h-3 w-3 mr-1" />
                        Reto
                      </Badge>
                      <Badge variant={patient.tiene_plan ? 'default' : 'outline'}>
                        {patient.tiene_plan ? 'Con plan alimenticio' : 'Sin plan'}
                      </Badge>
                      {patient.ultima_atencion && (
                        patient.ultima_consulta_pagada ? (
                          <Badge className="bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-300">
                            {[
                              'Pagado',
                              patient.ultimo_pago_monto != null ? `$${patient.ultimo_pago_monto}` : null,
                              METODO_PAGO_LABEL[patient.ultimo_pago_metodo],
                            ].filter(Boolean).join(' · ')}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/15 text-amber-700 ring-1 ring-inset ring-amber-500/25 dark:text-amber-300">
                            Última consulta sin pago
                          </Badge>
                        )
                      )}
                      {patient.ultima_atencion && (
                        <span className="text-[11.5px] text-muted-foreground tnum">
                          Atendido el {patient.ultima_atencion}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Aviso de falta y botón de seguimiento: solo en la vista de prospectos */}
                  {esProspectos && (
                    <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-1.5">
                        {patient.reto_21_activo && (
                          <Badge className="bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-300">
                            <Flame className="h-3 w-3 mr-1" /> Reto 21 días
                          </Badge>
                        )}
                        {patient.tuvo_cita_no_asistida && (
                          <Badge className="bg-amber-500/15 text-amber-700 ring-1 ring-inset ring-amber-500/25 dark:text-amber-300">
                            Ya tuvo cita y no asistió
                          </Badge>
                        )}
                        {!patient.tiene_seguimiento && (
                          <Badge className="bg-sky-500/10 text-sky-700 ring-1 ring-inset ring-sky-500/25 dark:text-sky-300">
                            Nuevo · sin contactar
                          </Badge>
                        )}
                        {patient.ultimo_contacto && (
                          <span className="text-[11.5px] text-muted-foreground tnum self-center">
                            {patient.tiene_seguimiento ? 'Último contacto' : 'Llegó el'} {patient.ultimo_contacto.slice(0, 10)}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSeguimientoPatient(patient); }}
                        data-testid={`seguimiento-patient-${patient.id}`}
                      >
                        <NotebookPen className="h-3.5 w-3.5 mr-1.5" />
                        Registrar seguimiento
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </CardContent>

          {/* Paginación */}
          {!searchInput && (page > 0 || hasMore) && (
            <div className="flex items-center justify-center gap-3 p-4 border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => {
                  const newPage = page - 1;
                  setPage(newPage);
                  fetchPatients(newPage, searchTerm, soloAtendidos, soloConFalta, filtroSeguimiento);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
              </Button>
              <span className="text-sm text-muted-foreground">Página {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore}
                onClick={() => {
                  const newPage = page + 1;
                  setPage(newPage);
                  fetchPatients(newPage, searchTerm, soloAtendidos, soloConFalta, filtroSeguimiento);
                }}
              >
                Siguiente <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </Card>

        {/* Diálogo de alta/edición (componente reutilizable) */}
        <PatientFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          patient={editingPatient}
          onSaved={handleSaved}
        />

        {esProspectos && (
          <SeguimientoDialog
            open={!!seguimientoPatient}
            onOpenChange={(open) => !open && setSeguimientoPatient(null)}
            patient={seguimientoPatient}
            onChange={() => fetchPatients(page, searchTerm, soloAtendidos, soloConFalta, filtroSeguimiento)}
          />
        )}

        <ConfirmModal
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          title="¿Eliminar paciente?"
          description={patientToDelete
            ? `Se eliminará a ${patientToDelete.nombre} ${patientToDelete.apellido} junto con su historial clínico, odontograma y archivos. Esta acción no se puede deshacer.`
            : ''}
          variant="danger"
          confirmLabel="Sí, eliminar"
          onConfirm={handleConfirmDelete}
        />
      </div>
    </Layout>
  );
};

/** Chip de filtro: activo = tinte de marca, inactivo = contorno discreto. */
const FiltroChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className={cn(
      'rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors',
      active
        ? 'bg-primary/[0.10] text-primary ring-1 ring-inset ring-primary/30'
        : 'bg-card text-muted-foreground ring-1 ring-inset ring-border hover:text-foreground hover:ring-primary/25',
    )}
  >
    {children}
  </button>
);
